import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { buyCredits, management_phone } from "../Config/config";
import axios from "axios";
import HeroSec from "../src/pages/home/HeroSec";
import { routeNames } from "../routeSegments";

export const useManagementPhoneAccess = (investorId, invType) => {
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState({});
  const [loadingPhone, setLoadingPhone] = useState({});
  const [pendingPhoneRequest, setPendingPhoneRequest] = useState(null);
  const [buyLoading, setBuyLoading] = useState({});
  const [creditInfo, setCreditInfo] = useState({ totalcredit: 0, usedcredit: 0, is_trail: true });

  const handlePhoneClick = async (info, index, checkCreditFlag = true) => {
    if (phoneNumbers[index] || loadingPhone[index]) return;
    
    setLoadingPhone(prev => ({ ...prev, [index]: true }));
    
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(management_phone, 
        { 
          id: info.id,
          checkcredit: checkCreditFlag,
          user_id: localStorage.getItem('user_id'),
          email: localStorage.getItem('LOGGED_IN_EMAIL'),
          redirect_url: investorId
            ? invType == "angel"
              ? `/pe${routeNames.investorById(investorId)}`
              : invType == "incubation"
                ? `/pe${routeNames.peIncubationInvestorById(investorId)}`
                : invType == "company"
                  ? routeNames.peCompanyFundById(investorId)
                  : invType == "cfs"
                    ? routeNames.cfsCompanyPageById(investorId)
                    : `/pe${routeNames.investorById(investorId)}`
            : window.location.href,
          category: invType == "company"
            ? "company"
            : invType == "incubation"
              ? "incubator"
              : invType == "cfs"
                ? "cfs"
                : "investor",
          company_id: localStorage.getItem('company_id'),
          cfs_company_id: localStorage.getItem('cfs_company_id')
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      const { data } = response;

      // If this is a credit check call
      if (checkCreditFlag) {
        if (data?.message === "Sufficient credits.") {
          // Store credit info and request info for later use
          setCreditInfo({ totalcredit: data?.totalcredit, usedcredit: data?.usedcredit, is_trail: data?.is_aplo_trail });
          setPendingPhoneRequest({ info, index });
          setShowConfirmPopup(true);
        } else if (data?.message === "Insufficient credits.") {
          setShowPaymentPopup(true);
        } else {
          toast.error(data?.message || 'Failed to check credits');
        }
      } else {
        // This is the actual phone number request (checkCredit: false)
        if (data?.success) {
          setShowConfirmPopup(false);
          setPhoneNumbers(prev => ({ ...prev, [index]: data.phone }));
          toast.success(data.message || 'Phone number retrieved successfully!');
        } else {
          setShowConfirmPopup(false);
          toast.error(data?.message || 'Failed to retrieve phone number');
        }
      }
    } catch (error) {
      console.error('Error fetching phone number:', error);
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error;
        
        if (status === 401) {
          toast.error('Authentication failed. Please login again.');
        } else if (status === 403) {
          toast.error('Access denied. You don\'t have permission.');
        } else if (status === 404) {
          toast.error(message || 'Phone number not found.');
        } else if (status >= 500) {
          toast.error('Server error. Please try again later.');
        } else {
          toast.error(message || `Request failed with status ${status}`);
        }
      } else if (error.request) {
        // Request made but no response received
        toast.error('Network error. Please check your connection.');
      } else {
        // Something else happened
        toast.error('An unexpected error occurred.');
      }
    } finally {
      setLoadingPhone(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleBuy = async (credit_units, credit_amount) => {
    setBuyLoading(prev => ({ ...prev, [credit_units]: true }));
    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        credit_units,
        credit_amount,
        user_id: localStorage.getItem('user_id'),
        usr_name: localStorage.getItem('user_name'),
        usr_email: localStorage.getItem('LOGGED_IN_EMAIL'),
        redirect_url: window.location.href,
        company_id: localStorage.getItem('company_id')
      };

      // Helper to read CSRF token from cookies (if your backend uses cookie CSRF)
      const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? decodeURIComponent(match[2]) : null;
      };
      const csrftoken = getCookie('csrftoken');

      const response = await axios.post(buyCredits,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(csrftoken ? { 'X-CSRFToken': csrftoken } : {})
          },
          withCredentials: true
        }
      );

      const data = response?.data;
      toast.success(data?.message || 'Purchase initiated');

      // Attempt to open a payment URL if the API returned one
      const paymentUrl = data?.payment_url;
      if (paymentUrl) {
        window.open(paymentUrl, '_blank');
      }
    } catch (error) {
      console.error('Error initiating purchase:', error);
      const msg = error?.response?.data?.message || error?.message || 'Failed to initiate purchase';
      toast.error(msg);
    } finally {
      setBuyLoading(prev => ({ ...prev, [credit_units]: false }));
    }
  };

  const onGetPhoneNumber = (info, idx) => {
    // Start with credit check (checkcredit: true)
    handlePhoneClick(info, idx, true);
  };

  const confirmPopup = () => {
    const { totalcredit, usedcredit, is_trail } = creditInfo;
    console.log('Rendering confirm popup', totalcredit, usedcredit);
    return (
      <div className="fixed top-0 left-0 w-screen h-screen bg-[rgba(0,0,0,0.5)] z-[99999] flex items-center justify-center"
        onClick={() => { setShowConfirmPopup(false); }}
      >
        <div
          style={{
            background: 'white',
            padding: '32px',
            borderRadius: '8px',
            maxWidth: '400px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            textAlign: 'center'
          }}
          onClick={e => e.stopPropagation()}
        >
          <h3 
            style={{
              fontSize: '18px',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#333'
            }}
          >
            Phone Number Access
          </h3>
          <p 
            style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}
          >
            {is_trail && (usedcredit==0) ? (
              <>You have 10 free phone number views. Each reveal costs 1 credit. Click proceed to start accessing.</>
            ) : (
              <>You have {totalcredit-usedcredit} out of {totalcredit} {is_trail ? 'free' : ''} credits. Each reveal costs 1 credit. Click to proceed with accessing the phone number.</>
            )}
          </p>
          <div className="flex flex-row gap-4 items-center justify-center">
            <button className="px-4 py-2 bg-[#a07024] text-white border-none rounded-md cursor-pointer text-sm font-medium"
                onClick={() => {
                    if (pendingPhoneRequest) {
                        setShowConfirmPopup(false);
                        // Make API call with checkcredit: false
                        handlePhoneClick(pendingPhoneRequest.info, pendingPhoneRequest.index, false);
                    }
                }}
                >
                Proceed
            </button>
            <button className="px-4 py-2 bg-[#a07024] text-white border-none rounded-md cursor-pointer text-sm font-medium"
                onClick={() => setShowConfirmPopup(false)}
                >
                Cancel
            </button>
           </div>
        </div>
      </div>
    );
  };

  const mailSubjects = [
    {
      id: 1,
      title: "Buy more credits",
      subject: "Interested in buying more credits to access Investor contacts.",
      body: "Hi Team,\n\nPlease raise an invoice for INR ......\n\nThank you.",
    }
  ];

  const paymentPopup = () => {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          zIndex: 11000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={() => setShowPaymentPopup(false)}
      >
        <div
          style={{
            background: '#fff',
            padding: 24,
            borderRadius: 8,
            width: 'min(520px, 205vw)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
          }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{justifyContent: 'center'}}>
            <span style={{margin: 0, marginBottom: 8, fontSize: 20}}>Get More Credits</span>
          </div>
          <p style={{marginTop: 0, color: '#555', marginBottom: 18}}>
            You have reached your free limit. Purchase credits to continue viewing verified phone numbers.
          </p>
          <div style={{display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, justifyContent: 'center'}}>
            <p>Buy 25 credits - INR 1500 *</p>
            <button
              onClick={() => handleBuy(25, 1500)}
              disabled={!!buyLoading[25]}
              style={{
                background: '#1f7a1f',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '3px 16px',
                cursor: buyLoading[25] ? 'not-allowed' : 'pointer',
                marginLeft: 35,
                opacity: buyLoading[25] ? 0.7 : 1
              }}
            >
              {buyLoading[25] ? 'Processing...' : 'Buy'}
            </button>
            <p>Buy 50 credits - INR 3000 *</p>
            <button
              onClick={() => handleBuy(50, 3000)}
              disabled={!!buyLoading[50]}
              style={{
                background: '#1f7a1f',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '3px 16px',
                cursor: buyLoading[50] ? 'not-allowed' : 'pointer',
                marginLeft: 35,
                opacity: buyLoading[50] ? 0.7 : 1
              }}
            >
              {buyLoading[50] ? 'Processing...' : 'Buy'}
            </button>
          </div>  
          <div >
            * <span style={{fontSize: 13, color: '#666'}}>Introductory price</span>
          </div>
          <div style={{fontSize: 13, color: '#666', marginBottom: 12, justifyContent: 'center', display: 'flex'}}>
            <span>
              <span 
                className="underline"
                style={{ color: '#2563eb', cursor: 'pointer' }}
                onClick={() => { handlePricingClick(mailSubjects[0]); setShowPaymentPopup(false); }}
              >Click here</span> to raise an invoice
              </span>
          </div>
          
          <div style={{display: 'flex', justifyContent: 'center', gap: 8}}>
            <button
              onClick={() => setShowPaymentPopup(false)}
              style={{
                background: '#a07924',
                border: 'none',
                padding: '5px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                color: '#ffffffff'
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const [showEmailOptions, setShowEmailOptions] = useState(false);
  
    // Smart Gmail detection
    const detectLikelyGmailUser = () => {
      const isAndroid = /Android/.test(navigator.userAgent);
      const hasGoogleServices = !!window.gapi;
      const isSignedIntoGoogle = window.gapi?.auth2?.getAuthInstance?.()?.isSignedIn?.get?.();
      
      return isSignedIntoGoogle || (isAndroid && hasGoogleServices);
    };
  
    const handlePricingClick = (mailData) => {
      const savedProvider = localStorage.getItem('preferredEmailProvider');
      
      // Don't persist 'copy' as a preference - always show options if last action was copy
      if (savedProvider && savedProvider !== 'copy') {
        openEmailProvider(savedProvider, mailData);
      } else {
        // Smart detection for first-time users or if last action was copy
        const likelyGmail = detectLikelyGmailUser();
        if (likelyGmail && savedProvider !== 'copy') {
          openGmail(mailData);
          localStorage.setItem('preferredEmailProvider', 'gmail');
        } else {
          setShowEmailOptions(true);
        }
      }
    };
  
    const openGmail = (mailData) => {
      // Gmail-specific encoding for better compatibility
      const subject = encodeURIComponent(mailData.subject).replace(/'/g, '%27').replace(/"/g, '%22');
      const body = encodeURIComponent(mailData.body).replace(/'/g, '%27').replace(/"/g, '%22');
      
      const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=sales@ventureintelligence.com&su=${subject}&body=${body}`;
      window.open(gmailUrl, '_blank');
    };
  
    const openOutlook = (mailData) => {
      const outlookUrl = `https://outlook.live.com/mail/0/deeplink/compose?to=sales@ventureintelligence.com&subject=${encodeURIComponent(mailData.subject)}&body=${encodeURIComponent(mailData.body)}`;
      window.open(outlookUrl, '_blank');
    };
  
    const openMailto = (mailData) => {
      const subject = encodeURIComponent(mailData.subject);
      const body = encodeURIComponent(mailData.body);
      window.location.href = `mailto:sales@ventureintelligence.com?subject=${subject}&body=${body}`;
    };
  
    const copyEmailDetails = async (mailData) => {
      const emailContent = `To: sales@ventureintelligence.com\nSubject: ${mailData.subject}\n\n${mailData.body}`;
      try {
        await navigator.clipboard.writeText(emailContent);
        alert('Email details copied to clipboard!');
      } catch (err) {
        alert(`Please send email to:\n\nsales@ventureintelligence.com\n\nSubject: ${mailData.subject}\n\n${mailData.body}`);
      }
    };
  
    const openEmailProvider = (provider, mailData) => {
      switch (provider) {
        case 'gmail': return openGmail(mailData);
        case 'outlook': return openOutlook(mailData);
        case 'mailto': return openMailto(mailData);
        case 'copy': return copyEmailDetails(mailData);
        default: return openMailto(mailData);
      }
    };
  
    const selectEmailProvider = (provider, mailData) => {
      localStorage.setItem('preferredEmailProvider', provider);
      setShowEmailOptions(false);
      openEmailProvider(provider, mailData);
    };

    const emailSelector = () => {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Choose your email provider</h3>
            <div className="space-y-3">
              <button
                onClick={() => selectEmailProvider('gmail', mailSubjects[0])}
                className="w-full p-3 text-left rounded-lg border hover:bg-gray-50 text-gray-900"
              >
                📧 Open in Gmail
              </button>
              <button
                onClick={() => selectEmailProvider('outlook', mailSubjects[0])}
                className="w-full p-3 text-left rounded-lg border hover:bg-gray-50 text-gray-900"
              >
                📫 Open in Outlook
              </button>
              <button
                onClick={() => selectEmailProvider('mailto', mailSubjects[0])}
                className="w-full p-3 text-left rounded-lg border hover:bg-gray-50 text-gray-900"
              >
                📬 Use default email client
              </button>
              <button
                onClick={() => selectEmailProvider('copy', mailSubjects[0])}
                className="w-full p-3 text-left rounded-lg border hover:bg-gray-50 text-gray-900"
              >
                📋 Copy email details
              </button>
            </div>
            <button
              onClick={() => setShowEmailOptions(false)}
              className="mt-4 w-full py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
    )};

  return {
    // State
    showConfirmPopup,
    showPaymentPopup,
    phoneNumbers,
    loadingPhone,
    showEmailOptions,
    
    // Functions
    onGetPhoneNumber,
    handlePhoneClick,
    
    // Components
    confirmPopup,
    paymentPopup,
    emailSelector,
    
    // Setters for external control
    setShowConfirmPopup,
    setShowPaymentPopup
  };
};